import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getExperiences } from '../data/experiences.index';
import { getEducation } from '../data/education.index';
import { getStoryPillars, getLeadershipProfile } from '../data/skillsStory.index';
import CVDownload from './CVDownload';
import { useLanguage } from '../context/LanguageContext';

function SummarySection() {
  const { t, lang } = useLanguage();
  const prefix = useMemo(() => (lang === 'ar' ? '/ar' : ''), [lang]);
  const withPrefix = (path) => {
    if (path === '/' || path === '') return prefix || '/';
    return `${prefix}${path}`.replace(/\/+/g, '/');
  };

  const experiences = useMemo(() => getExperiences(lang), [lang]);
  const education = useMemo(() => getEducation(lang), [lang]);
  const storyPillars = useMemo(() => getStoryPillars(lang), [lang]);
  const leadership = useMemo(() => getLeadershipProfile(lang), [lang]);

  const recentExperiences = experiences.slice(0, 2);
  const recentEducation = education.slice(0, 1);

  return (
    <div className="space-y-16 animate-fade-in text-white">
      {/* Engineering Pillars & Storytelling Preview */}
      <section className="space-y-8">
        <div className="flex items-end justify-between px-2">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
              {leadership.role}
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
              {t('sections.skillsTitle')}
            </h3>
            <div className="h-1 w-16 bg-primary-500 rounded-full"></div>
          </div>
          <Link 
            to={withPrefix('/skills')} 
            className="text-sm font-semibold text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1 group"
          >
            {t('sections.viewAll')}
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        {/* 3 Domain Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {storyPillars.map((pillar, idx) => (
            <div 
              key={pillar.id}
              className="p-7 rounded-3xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.05] transition-all duration-300 flex flex-col justify-between space-y-6 group shadow-lg"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary-400">
                    0{idx + 1} • {pillar.category}
                  </span>
                  <span className="w-2 h-2 rounded-full bg-primary-400/60 group-hover:bg-primary-400 transition-colors"></span>
                </div>
                <h4 className="text-lg font-bold text-white group-hover:text-primary-300 transition-colors leading-snug">
                  {pillar.headline}
                </h4>
                <p className="text-sm text-white/70 leading-relaxed line-clamp-3">
                  {pillar.approach}
                </p>
              </div>

              {/* Sample Stack Badges */}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {pillar.stack.slice(0, 3).map((item, sIdx) => (
                    <span 
                      key={sIdx} 
                      className="px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-xs font-medium text-white/80"
                    >
                      {item.name.split('(')[0].trim()}
                    </span>
                  ))}
                  {pillar.stack.length > 3 && (
                    <span className="px-2 py-1 rounded-lg bg-primary-500/10 text-xs text-primary-400 font-medium">
                      +{pillar.stack.length - 3} {t('sections.moreSuffix')}
                    </span>
                  )}
                </div>

                <Link
                  to={withPrefix('/skills')}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-400 hover:text-primary-300 transition-colors pt-1"
                >
                  <span>{lang === 'ar' ? 'عرض دراسة الحالة والمعمارية ←' : 'Explore Story & Architecture →'}</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Brief */}
      <section className="space-y-8">
        <div className="flex items-end justify-between px-2">
          <div className="space-y-2">
            <h3 className="text-2xl font-bold text-white tracking-tight">{t('sections.recentExperienceTitle')}</h3>
            <div className="h-1 w-12 bg-primary-500 rounded-full"></div>
          </div>
          <Link 
            to={withPrefix('/experience')} 
            className="text-sm font-semibold text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1 group"
          >
            {t('sections.viewAll')}
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {recentExperiences.map((exp, idx) => (
            <div key={idx} className="p-7 rounded-3xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.05] hover:border-white/20 transition-all group shadow-md">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div>
                  <h4 className="font-bold text-lg text-white group-hover:text-primary-300 transition-colors">{exp.title}</h4>
                  <p className="text-sm text-primary-400 font-semibold">{exp.company}</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/60 whitespace-nowrap bg-white/10 px-2.5 py-1 rounded-md border border-white/10">
                  {exp.period}
                </span>
              </div>
              <p className="text-sm text-white/70 line-clamp-3 leading-relaxed">
                {exp.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Education Brief */}
      <section className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <div className="space-y-1">
            <h3 className="text-xl font-bold text-white tracking-tight">{t('sections.educationTitleSimple')}</h3>
            <div className="h-1 w-10 bg-primary-500 rounded-full"></div>
          </div>
          <Link to={withPrefix('/education')} className="text-xs font-semibold text-primary-400 hover:text-primary-300 transition-colors">
            {t('sections.viewAll')}
          </Link>
        </div>
        {recentEducation.map((edu, idx) => (
          <div key={idx} className="p-6 rounded-2xl bg-white/[0.03] border border-white/10 space-y-2 hover:bg-white/[0.05] transition-colors group">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <h4 className="text-base font-bold text-white group-hover:text-primary-300 transition-colors">{edu.degree}</h4>
              <span className="text-xs text-white/60 uppercase tracking-wider">{edu.period}</span>
            </div>
            <p className="text-sm text-primary-400/90 font-medium">{edu.institution}</p>
          </div>
        ))}
      </section>

      {/* CV Download Section */}
      <section className="relative p-8 md:p-12 rounded-[2.5rem] bg-gradient-to-r from-white/[0.04] to-white/[0.02] border border-white/10 overflow-hidden group shadow-xl">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="space-y-6 text-center md:text-left">
            <h3 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
              {t('sections.cvDownloadTitle')}
            </h3>
            <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed">
              {t('sections.cvDownloadDesc')}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              {[t('sections.cvProExp'), t('sections.cvEduDetails'), t('sections.cvTechSkills')].map((item, i) => (
                <span key={i} className="flex items-center gap-2 text-white/80 text-xs md:text-sm bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </span>
              ))}
            </div>
          </div>
          <div className="shrink-0">
            <CVDownload />
          </div>
        </div>
      </section>
    </div>
  );
}

export default SummarySection;
